import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PointClick } from '../../class/Geo';
import { Landmark } from '../../database/models';
import LandmarksService from '../../services/landmarks.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { MapService } from '../../services/map.service';

import { LayoutComponent } from '../layout/layout.component';
import { LeftPanelComponent } from '../layout/left-panel/left-panel.component';
import { SubNavigationItemComponent } from '../layout/sub-navigation/sub-navigation-item/sub-navigation-item.component';
import { SubNavigationComponent } from '../layout/sub-navigation/sub-navigation.component';
import { TitleSectionComponent } from '../layout/title-section/title-section.component';
import { TopBarComponent } from '../layout/top-bar/top-bar.component';

import { TranslatePipe } from '@ngx-translate/core';
import { ContainerComponent } from '../layout/container/container.component';
import { ActionsComponent } from '../layout/title-section/actions/actions.component';
import { TitleComponent } from '../layout/title-section/title/title.component';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LANDMARKS_CATEGORY_LANDMARK } from '../management-tab/landmarks-sub-tab/landmarks-routing.constants';
import { DeviceCardComponent } from '../shared/card/device-card/device-card.component';
import { LandmarkCardComponent } from '../shared/card/landmark-card/landmark-card.component';

@Component({
  selector: 'around',
  templateUrl: './around.component.html',
  styleUrls: ['./around.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LayoutComponent,
    TopBarComponent,
    LeftPanelComponent,
    TitleSectionComponent,
    SubNavigationComponent,
    SubNavigationItemComponent,
    TranslatePipe,
    ContainerComponent,
    TitleComponent,
    ActionsComponent,
    LandmarkCardComponent,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    DeviceCardComponent,
  ],
})
export class AroundComponent implements OnDestroy, OnInit {
  public mapService = inject(MapService);
  private _landmarksService = inject(LandmarksService);
  public $landmarksById = this._landmarksService.$landmarksById.asReadonly();
  private loadingService = inject(LoadingService);
  private _router = inject(Router);

  private poiEventSubscriber: Subscription;
  public devicesList = 5;
  public poiList = 5;
  public activeType: string;
  private intervalClosestDevices: ReturnType<typeof setInterval>;
  private intervalClosestPois: ReturnType<typeof setInterval>;

  public displayRealDistance = this.mapService.$displayRoute;
  public realClosestData = this.mapService.$realClosest;

  ngOnInit() {
    if (this.$landmarksById()) {
      this.initComponent();
    } else {
      this.poiEventSubscriber = this.loadingService.poiCatsLoadedEvent$.subscribe(() => {
        this.initComponent();
        this.poiEventSubscriber.unsubscribe();
      });
    }
  }

  private initComponent() {
    if (!this.mapService.chosenPoi) {
      this.mapService.chosenPoi = LocalStorageService.getElement('devicesAround')
        ? (LocalStorageService.getElement('devicesAround')?.poi as Landmark)
        : undefined;
      if (this.mapService.chosenPoi) {
        this.center(this.mapService.chosenPoi);
        this.mapService.drawMarkerPosition(
          new PointClick({
            lat: this.mapService.chosenPoi.latitude,
            lon: this.mapService.chosenPoi.longitude,
            addr: this.mapService.chosenPoi.address1,
            city: this.mapService.chosenPoi.city,
            postcode: this.mapService.chosenPoi.postal_code,
          })
        );
      }
      this.mapService.closestDevice = LocalStorageService.getElement('devicesAround')
        ? LocalStorageService.getElement('devicesAround')?.devices
        : [];
      this.mapService.closestPoi = LocalStorageService.getElement('poisAround')
        ? LocalStorageService.getElement('poisAround')?.pois
        : [];
      if (
        !this.mapService.chosenPoi &&
        !this.mapService.closestDevice?.length &&
        !this.mapService.closestPoi?.length
      ) {
        this.close();
      }
    }
    if (this.mapService.chosenPoi) {
      this.pollingAround();
    }
  }

  ngOnDestroy() {
    this.mapService.clearETA();
    this.mapService.chosenPoi = undefined;
    this.mapService.removePosition();
    clearInterval(this.intervalClosestDevices);
    clearInterval(this.intervalClosestPois);
  }

  private center(poi: Landmark) {
    this.mapService.fitOn(poi.latitude, poi.longitude);
  }

  public close() {
    this._router.navigate(['/dashboard']);
    this.mapService.clearLayer('hLayer');
  }

  public moreElements(type: string) {
    if (type === 'device') {
      this.devicesList = 10;
    } else {
      this.poiList = 10;
    }
    this.refreshItineraries(type);
  }

  public lessElements(type: string) {
    if (type === 'device') {
      this.devicesList = 5;
    } else {
      this.poiList = 5;
    }
    this.refreshItineraries(type);
  }

  public refreshItineraries(type: string) {
    if (this.mapService.$displayRoute()) this.goToETA(type);
  }

  public goToETA(type: string) {
    this.activeType = type;
    this.mapService.addETA(
      this.mapService.chosenPoi,
      type === 'device'
        ? this.mapService.closestDevice.slice(0, this.devicesList)
        : this.mapService.closestPoi.slice(0, this.poiList),
      type
    );
  }

  public clearETAAround() {
    this.mapService.clearETA();
    this.activeType = undefined;
  }

  private pollingAround() {
    this.intervalClosestDevices = setInterval(
      () => this.mapService.getClosestDeviceOrPoi('device', this.mapService.chosenPoi, 10, undefined, true),
      20000
    );
    this.intervalClosestPois = setInterval(
      () => this.mapService.getClosestDeviceOrPoi('poi', this.mapService.chosenPoi, 10, undefined, true),
      20000
    );
  }

  public navigateToLandmark(uid: number, uid_category: number) {
    this._router.navigate([LANDMARKS_CATEGORY_LANDMARK(uid_category, uid)], { queryParams: { view: 'map' } });
  }
}
